import { useEffect, useState } from 'react';
import { useLabels } from 'wsm-common-data';
import logger from 'ws-scripts/modules/logger';

const LOG_PREFIX = '[ws-locations-control][useLocationCountDisplay]';

/**
 * Hook that manages location count display concerns.
 * Responsibilities:
 * - Formats the location count display string (and handles errors)
 * - Manages visibility state
 * - Notifies the parent via onLocationStatusChange
 *
 * @param {Object} params
 * @param {number} params.locationCount - The number of locations found.
 * @param {string|null} params.error - Any error from the search.
 * @param {boolean} params.isLoading - Whether the search is in progress.
 * @param {Function} [params.onLocationStatusChange] - Callback when location availability changes.
 * @returns {{ locationCountString: string|number, isHidden: boolean, locationLabel: string }}
 */
export const useLocationCountDisplay = ({
	locationCount,
	error,
	isLoading,
	onLocationStatusChange
}) => {
	const labels = useLabels();
	const [locationCountString, setLocationCountString] = useState('');

	// Handle location count changes and notify parent
	useEffect(() => {
		const hasLocations = !error && locationCount > 0;

		if (error || !locationCount) {
			setLocationCountString(labels.get('NO'));
		} else {
			setLocationCountString(locationCount);
		}

		// Notify parent component about location availability
		if (onLocationStatusChange) {
			onLocationStatusChange(hasLocations);
		}
	}, [locationCount, error, labels, onLocationStatusChange]);

	// Manage visibility state
	const isHidden = isLoading || locationCountString === '';

	// Compute formatted label
	const locationLabel = locationCountString
		? labels.get('LOCATIONS_NEAR_YOU').toLowerCase()
		: '';

	logger.debug(`${LOG_PREFIX} Returning count: ${locationCountString}`);

	return {
		locationCountString,
		isHidden,
		locationLabel
	};
};
